// The Build/Brand message catalog (Slice 5 Flows 3-6; docs/phase-0/slice5-ux-flows.md,
// docs/phase-0/slice5-component-designs.md). No copywriter draft file exists yet for
// Slice 5 (verified: docs/copy/ contains only slice4-certify-copy.md) - this mirrors
// apps/web/src/copy/certify.ts's own precedent exactly: every string the ux spec tags
// `[copy: flagged for review]` is reproduced VERBATIM here and rendered as DRAFT, never
// finalized by this build lane (CLAUDE.md §8); every other key is a plain structural
// label (a button name, a heading), not a new compliance/legal claim, so it carries no
// separate flag, per certify.ts's own bare-label convention (its own header, certify.ts:
// 1-31).
//
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - the same gap certify.ts's header names: no
// i18n-routed ICU MessageFormat mechanism exists in this repo yet (the i18n-engineer's
// lane, `i18n-localization`). `buildCopy()` below does plain `{token}` substitution only
// - no ICU plural rules.
//
// NEVER INVENTED HERE (CLAUDE.md §8 hard stop): the D55/D56 add-on price (still BLOCKED
// - owner-supplied, no figure exists anywhere in canon docs yet). `allowanceExhausted`
// below (design-review W5) carries no `{price}` slot and no bracketed placeholder token
// for it; it states only the qualitative fact D55/D56 already establish - "a small, flat
// ... add-on, chosen visibly, never a silent meter" (docs/phase-0/workflows.md:42) -
// never a number. The hard-gate/manual-step reasoning (structural copy only - the actual
// reason enum value is supplied by the caller); the promote-confirm and "not yet
// validated"-adjacent wording, every one flagged `[copy: flagged for review]` below
// exactly as the ux spec states it, for the copywriter + `§6.6` expert review before ship.
//
// ADR-0022 (Slice 5 close): the Production Build path and the build hold-RESOLUTION
// routes are deferred to Slice 18, coupled to real build execution - see the ADR and
// `api/build.ts`'s own header. The `build.production.checklist.*`/`ladder.*`/
// `promoteCta`/`promoteConfirm.*`/`promoted.*`/`deniedRole`/`workflowPending*` keys below
// describe that SAME still-planned feature, so they stay defined (unused meanwhile,
// ready to re-wire when Slice 18 lands) rather than deleted and re-written later. The
// `build.production.deferred.*` / `build.mvp.hardGate.resolutionDeferred` /
// `build.mvp.hardGate.acknowledgeCta` / `build.mvp.manualStep.resolutionDeferred` keys
// ARE new, authored directly for this honesty fix (not ux-spec-sourced): each states a
// plain roadmap fact ("becomes available with real build execution"), never a
// figure, a date, or a legal/compliance claim, so none carries a `§6.6` review flag -
// still a stop-slop pass before ship like every other string here.
export const BUILD_COPY = {
  // ---- Brand direction picker (Flow 3, ux spec §6.1-6.2) ----
  "build.brand.directions.title": "Choose a brand direction",
  "build.brand.directions.loading": "Your crew is sketching brand directions for {appName}...",
  "build.brand.directions.error": "We couldn't generate directions. Try again.",
  "build.brand.directions.regenerating": "Trying a new direction...",
  "build.brand.directions.roundCounter": "Round {round} of {total} included",
  // [copy: flagged for review] - reworded from the ux spec's §6.2 price-slotted draft
  // (design-review W5): the exact price stays BLOCKED/owner-supplied (CLAUDE.md §8) with
  // no figure invented and no bracketed placeholder token shown to the user; this states
  // only the qualitative D55/D56 fact ("a small, flat ... add-on", workflows.md:42).
  "build.brand.directions.allowanceExhausted":
    "You've used your two included refinement rounds. Adding another round is a small, flat add-on.",
  "build.brand.directions.addonCta": "Add a round",
  "build.brand.directions.chooseCta": "Choose this direction",
  "build.brand.directions.chosenConfirmation": "Nice - building {appName}'s look now.",
  "build.brand.directions.deniedRole": "Only an owner or admin can choose a brand direction.",
  "build.brand.directions.deniedSequence": "Get a verdict for {appName} first.",

  // ---- Mockup review + approval gate (Flow 3, ux spec §6.3-6.4) ----
  "build.brand.mockups.title": "Review your mockups",
  "build.brand.mockups.loading": "Building example screens in {appName}'s new look...",
  "build.brand.mockups.error": "We couldn't build your mockups. Try again.",
  "build.brand.mockups.tweaking": "Updating...",
  "build.brand.mockups.tweakCta": "Tweak",
  "build.brand.mockups.regenerateCta": "Regenerate the set",
  "build.brand.mockups.gate.body": "Nothing is built until you approve this look.",
  "build.brand.mockups.gate.approveCta": "Approve and continue to Build",
  "build.brand.mockups.gate.keepRefiningCta": "Keep refining",
  // [copy: flagged for review] - verbatim, ux spec §6.4.
  "build.brand.mockups.gate.confirmBody": "Approve this look for {appName}? Build starts from here.",
  "build.brand.mockups.gate.tweakInFlightNote": "Finish the current tweak first.",
  "build.brand.mockups.approvedConfirmation":
    "Approved. Let's confirm how {appName} should work before we build it.",
  "build.brand.mockups.deniedRole": "Only an owner or admin can approve this look.",

  // ---- Guided workflow validation (Flow 4, ux spec §7) ----
  "build.workflow.titleGeneric": "Confirm how your app works",
  "build.workflow.title": "Confirm how {appName} works",
  "build.workflow.subtitle": "Let's make sure we've got {appName} right before we build it.",
  "build.workflow.rerunSubtitle":
    "A few new things to confirm before we harden {appName} for production.",
  "build.workflow.error": "We couldn't pull up your app's workflows. Try again.",
  "build.workflow.confirmCta": "Confirm: {statement}",
  "build.workflow.adjustCta": "Adjust: {statement}",
  "build.workflow.adjustPrompt": "What should this actually do?",
  "build.workflow.saveAdjustmentCta": "Save",
  "build.workflow.confirmedChip": "Confirmed",
  "build.workflow.editLink": "Edit",
  "build.workflow.continueCta": "Continue to Build",
  "build.workflow.continueDisabledReason": "Confirm every item first.",
  "build.workflow.deniedRole": "Only an owner or admin can confirm how {appName} should work.",

  // ---- MVP Build (Flow 5, ux spec §8) ----
  "build.mvp.titleGeneric": "Build your app",
  "build.mvp.title": "Build {appName}",
  "build.mvp.cloud.prompt": "Where should {appName} run?",
  "build.mvp.cloud.managed.name": "Managed host",
  "build.mvp.cloud.managed.description": "Fast, cheap default. Ready in minutes.",
  "build.mvp.cloud.production.name": "Your production cloud",
  "build.mvp.cloud.production.description":
    "Build straight onto your own cloud. No move later - we build portable either way.",
  "build.mvp.cloud.confirmCta": "Build {appName}",
  "build.mvp.building": "Building {appName}...",
  "build.mvp.statusReadout": "Building {appName}... currently: {stepLabel}",
  "build.mvp.step.failed": "One step didn't go through: {step}. Retrying won't lose your progress.",
  "build.mvp.step.retryCta": "Retry",
  "build.mvp.step.state.pending": "Queued",
  "build.mvp.step.state.running": "Running",
  "build.mvp.step.state.done": "Done",
  "build.mvp.step.state.failed": "Failed",
  "build.mvp.step.state.awaitingHardGate": "Needs your decision",
  "build.mvp.step.state.awaitingManualStep": "Needs one thing from you",
  "build.mvp.step.state.verifying": "Verifying",
  "build.mvp.hardGate.banner": "This step needs your decision.",
  "build.mvp.hardGate.openCta": "Open",
  // [copy: flagged for review] - verbatim shape, ux spec §8.3. {step}/{reason} are
  // structural substitutions, not invented wording.
  "build.mvp.hardGate.panelWhy": "{step} touches {reason}, so it always waits for you.",
  "build.mvp.hardGate.confirmCta": "Confirm",
  "build.mvp.hardGate.notNowCta": "Not now",
  // ADR-0022 - new, not ux-spec-sourced (see the file header): the hard-gate
  // hold-resolution route is deferred to Slice 18, so the dialog now only acknowledges
  // the hold rather than offering a confirm action that would call it.
  "build.mvp.hardGate.resolutionDeferred":
    "Resolving this step becomes available with real build execution, coming in a later phase.",
  "build.mvp.hardGate.acknowledgeCta": "Got it",
  "build.mvp.hardGate.reason.payments": "payments",
  "build.mvp.hardGate.reason.auth": "authentication",
  "build.mvp.hardGate.reason.pii": "personal data",
  "build.mvp.manualStep.heading": "One thing only you can do",
  // [copy: flagged for review] - verbatim, ux spec §8.3 (D78).
  "build.mvp.manualStep.body":
    "One thing only you can do: create an empty repo for {appName} on GitHub. We'll take it from there.",
  "build.mvp.manualStep.linkCta": "Create the empty repo",
  "build.mvp.manualStep.confirmCta": "I've done it",
  "build.mvp.manualStep.checking": "Checking...",
  "build.mvp.manualStep.notYetCreated": "Not created yet - try the link again.",
  // ADR-0022 - new, not ux-spec-sourced: the manual-step verify route is deferred to
  // Slice 18, so this replaces the "I've done it" confirm button (which would call it)
  // with an honest note. The link above still works - it is GitHub's own real page.
  "build.mvp.manualStep.resolutionDeferred":
    "Confirming this step becomes available with real build execution, coming in a later phase.",
  "build.mvp.refusedEntitlement.body": "Building for {target} needs {tier}. You're on {currentTier}.",
  "build.mvp.complete.body": "{appName} is live as an MVP. Your crew keeps working from here.",
  "build.mvp.complete.certifyCallout": "Certify is now available",
  "build.mvp.complete.continueCta": "Continue",
  "build.mvp.deniedRole": "Only an owner or admin can choose the cloud or confirm a build step.",
  "build.mvp.error": "We couldn't start your build. Try again.",

  // ---- The app-stage badge (Flow 5, ux spec §8.4) ----
  "build.stage.label.validated": "Validated",
  "build.stage.label.mvp": "MVP",
  "build.stage.label.production": "Production",
  "build.stage.unlockTrigger": "What does this unlock?",
  "build.stage.unlock.validated": "Build an MVP once your brand, mockups, and workflows are confirmed.",
  "build.stage.unlock.mvp":
    "Certify is available. Production-grade hardening can start when you're ready.",
  "build.stage.unlock.production":
    "Your app is live in production, with the full environment ladder cleared.",

  // ---- AppDetail Build tab (Flow 5 tail + Flow 6 entry, ux spec §2.4, §8.7) ----
  "build.tab.title": "Build",
  "build.tab.mvp.heading": "Refine the MVP",
  "build.tab.mvp.body": "Keep shaping {appName}. Start a Production Build once you're ready to harden it.",
  "build.tab.mvp.startProductionCta": "Start Production Build",
  "build.tab.production.heading": "This app is in Production",
  "build.tab.production.body": "{appName} is live. Your crew keeps it running from here.",
  "build.tab.production.certifyLinkCta": "Go to Certify",
  "build.tab.deniedRole": "Only an owner or admin can start a build.",

  // ---- Production Build (Flow 6, ux spec §9) ----
  "build.production.titleGeneric": "Harden your app for Production",
  "build.production.title": "Harden {appName} for Production",
  // ADR-0022: this screen only fetches the app itself now (the production-readiness
  // read is deferred, see the file header) - text updated so "readiness" isn't named
  // when nothing readiness-shaped is being loaded.
  "build.production.error": "We couldn't load this app. Try again.",
  "build.production.workflowPending": "Let's confirm what's changed before we harden {appName}.",
  "build.production.workflowPendingCta": "Confirm what's changed",
  "build.production.checklist.heading": "Hardening checklist",
  "build.production.ladder.heading": "Environment ladder",
  "build.production.ladder.progress": "Promoting {appName} through {env}...",
  "build.production.ladder.status.notReached": "Not yet reached",
  "build.production.ladder.status.current": "Current",
  "build.production.ladder.status.passed": "Passed",
  "build.production.promoteCta": "Promote to Production",
  "build.production.promoteConfirm.heading": "Promote {appName} to Production?",
  // [copy: flagged for review] - verbatim, ux spec §9.3.
  "build.production.promoteConfirm.body":
    "Promote {appName} to Production? This moves real traffic to your production cloud.",
  "build.production.promoteConfirm.confirmCta": "Promote to Production",
  "build.production.promoteConfirm.cancelCta": "Cancel",
  "build.production.promoted.body": "{appName} is in Production. Your crew keeps it running from here.",
  "build.production.deniedRole": "Only an owner or admin can harden or promote {appName}.",
  "build.production.stageGate.heading": "Production Build becomes available once {appName} reaches MVP.",
  "build.production.stageGate.body":
    "Keep building, and this unlocks automatically once the app moves past MVP.",
  "build.production.stageGate.buildLinkCta": "Go to Build",

  // ---- Production Build: honest deferred-state preview (ADR-0022) - shown once an app
  // reaches MVP stage (past the stageGate above), replacing the readiness fetch this
  // screen used to make. New copy, not ux-spec-sourced (see the file header). ----
  "build.production.deferred.heading": "Coming with real build execution",
  "build.production.deferred.body":
    "Production Build becomes available once real build execution ships in a later phase. Here's a preview of what's ahead for {appName}.",
  "build.production.deferred.checklistPreview":
    "Every hardening item, from rotated secrets to tested backups, will need to pass before {appName} can promote. Not yet actionable.",
  "build.production.deferred.ladderPreview":
    "{appName} will move through a staged environment ladder (dev, staging, pre-production, production) before real users see it. Not yet actionable.",

  // ---- Shared retry/error chrome ----
  "build.errors.generic.retryCta": "Try again",
} as const;

export type BuildCopyKey = keyof typeof BUILD_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (see the header note and
 * certify.ts's identical implementation, which this mirrors byte-for-byte). A missing
 * var leaves the literal `{token}` in place rather than throwing.
 */
export const buildCopy = (key: BuildCopyKey, vars?: Record<string, string | number>): string => {
  const template = BUILD_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
